// A fixed-capacity, per-frame rebuilt set of solid line segments on the emissive layer (lock-on lines, air drop
// lines). Buffers are allocated once; a frame writes begin() → push()… → end() without allocating.

import { AdditiveBlending, BufferAttribute, BufferGeometry, DynamicDrawUsage, LineSegments, ShaderMaterial, type Color, type Vector3 } from 'three'
import { InvariantViolation } from '@taxwar/net'
import { EMISSIVE_LAYER, assignLayer } from '../layers'
import { LINE_FRAGMENT, LINE_VERTEX } from './glyphShaders'

const COMPONENTS = 3
const SEGMENT_VERTICES = 2

/** Flags the first `length` array elements of `attribute` for upload. */
export function uploadPrefix(attribute: BufferAttribute, length: number): void {
  attribute.clearUpdateRanges()
  attribute.addUpdateRange(0, length)
  attribute.needsUpdate = true
}

export class LineBatch {
  readonly lines: LineSegments

  private readonly positions: BufferAttribute
  private readonly colours: BufferAttribute
  private readonly material: ShaderMaterial
  private count = 0

  constructor(
    private readonly capacity: number,
    intensity: number,
  ) {
    const geometry = new BufferGeometry()
    this.positions = new BufferAttribute(new Float32Array(capacity * SEGMENT_VERTICES * COMPONENTS), COMPONENTS).setUsage(DynamicDrawUsage)
    this.colours = new BufferAttribute(new Float32Array(capacity * SEGMENT_VERTICES * COMPONENTS), COMPONENTS).setUsage(DynamicDrawUsage)
    geometry.setAttribute('position', this.positions)
    geometry.setAttribute('aColor', this.colours)
    geometry.setDrawRange(0, 0)
    this.material = new ShaderMaterial({
      uniforms: { uIntensity: { value: intensity } },
      vertexShader: LINE_VERTEX,
      fragmentShader: LINE_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    })
    this.lines = new LineSegments(geometry, this.material)
    // Segments move every frame; a cached bounding sphere would cull them wrongly.
    this.lines.frustumCulled = false
    assignLayer(this.lines, EMISSIVE_LAYER)
  }

  begin(): void {
    this.count = 0
  }

  push(from: Vector3, to: Vector3, colour: Color): void {
    if (this.count >= this.capacity) throw new InvariantViolation(`line batch over its capacity of ${this.capacity}`)
    const vertex = this.count * SEGMENT_VERTICES
    this.positions.setXYZ(vertex, from.x, from.y, from.z)
    this.positions.setXYZ(vertex + 1, to.x, to.y, to.z)
    this.colours.setXYZ(vertex, colour.r, colour.g, colour.b)
    this.colours.setXYZ(vertex + 1, colour.r, colour.g, colour.b)
    this.count++
  }

  /** Uploads only the written segments. */
  end(): void {
    const vertices = this.count * SEGMENT_VERTICES
    this.lines.geometry.setDrawRange(0, vertices)
    if (vertices === 0) return
    uploadPrefix(this.positions, vertices * COMPONENTS)
    uploadPrefix(this.colours, vertices * COMPONENTS)
  }

  dispose(): void {
    this.lines.removeFromParent()
    this.lines.geometry.dispose()
    this.material.dispose()
  }
}
